import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary, Caption, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo } from 'react';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { useAppSelector } from '@/redux/hooks';
import { getLiveItemDataBidCount } from '@/redux/modules/console';
import {
    getItemSummaryBidCount,
    getItemSummaryCatalogId,
    getItemSummaryCurrency,
    getItemSummaryFirstImageUrl,
    getItemSummaryHighBidEstimate,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummaryLeadingBid,
    getItemSummaryLotNumber,
    getItemSummaryLowBidEstimate,
    getItemSummarySalePrice,
    getItemSummaryStartPrice,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { useTrackListItemClickedAnalytics } from '@/utils/amplitude/analytics/bidding/trackListItemClicked.analytics';
import { addImageParams } from '@/utils/urls';

import LotImageContainer from '../LotImageContainer/LotImageContainer';

type Props = {
    index: number;
    isLiveItemVisible: boolean;
    itemId: number;
    liveItemId: number;
    liveItemIndex: number;
    lotsUntilUpcoming: number;
};

const ListItem = ({ index, isLiveItemVisible, itemId, liveItemId, liveItemIndex, lotsUntilUpcoming }: Props) => {
    const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const highBidEstimate = useAppSelector((state) => getItemSummaryHighBidEstimate(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const lotNumber = useAppSelector((state) => getItemSummaryLotNumber(state, itemId));
    const lowBidEstimate = useAppSelector((state) => getItemSummaryLowBidEstimate(state, itemId));
    const salePrice = useAppSelector((state) => getItemSummarySalePrice(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));

    const liveItemBidCount = useAppSelector((state) => getLiveItemDataBidCount(state, itemId));
    const trackListItemClicked = useTrackListItemClickedAnalytics(itemId, catalogId);

    const url = useAppSelector((state) => getItemSummaryFirstImageUrl(state, itemId));
    const imageUrl = addImageParams({ height: 74, url, width: 74 });
    const isLiveItem = liveItemId === itemId;

    // Append spacing if "go to live item" overlay is visible and user is at top of list, prevents overlay from cutting off first item
    const showHeaderSpacer = index === 0 && !isLiveItemVisible;

    // Make list item a disabled color to indicate the item is unavailable to purchase
    const itemUnavailable = isSold || isPassed;
    const itemBidCount = isLiveItem ? liveItemBidCount : bidCount;

    return (
        <div
            className={cn(
                'flex min-h-[120px] flex-col border-b border-l-[4px] border-b-border-secondary border-l-transparent bg-background-primary px-md py-sm [&:hover_.heart-icon]:grid',
                { 'border-l-critical-500 bg-background-critical-light': isLiveItem }
            )}
            data-testid={`auctionLotScrollerItem-${index}`}
            key={liveItemIndex}
        >
            {showHeaderSpacer && <div className="h-[67px]" />}
            <div className="flex">
                <LotImageContainer
                    catalogId={catalogId}
                    imageUrl={imageUrl}
                    itemId={itemId}
                    itemUnavailable={itemUnavailable}
                />
                <div className={cn('flex flex-col', { 'opacity-50': itemUnavailable })}>
                    {isLiveItem && (
                        <BodySecondary
                            className="mb-2xs"
                            color="critical"
                        >
                            <FormattedMessage id="bidding_console.console.lotTracker.liveNow" />
                        </BodySecondary>
                    )}
                    {lotsUntilUpcoming && (
                        <BodySecondary
                            className="mb-2xs"
                            color="critical"
                        >
                            <FormattedMessage
                                id="bidding_console.console.status.nextFavorite.value"
                                values={{ total: lotsUntilUpcoming[itemId] }}
                            />
                        </BodySecondary>
                    )}
                    <SubtleMonochromeLink
                        className="mb-2xs line-clamp-2 capitalize"
                        href={`/item/${itemId}`}
                        onClick={trackListItemClicked}
                        target="_blank"
                    >
                        {`${lotNumber}: ${title}`}
                    </SubtleMonochromeLink>
                    {!isPassed && !isLiveItem && (
                        <BodySecondary
                            className="mb-2xs"
                            color="secondary"
                        >
                            <FormattedMessage
                                id="bidding_console.console.description.estimate_range"
                                values={{
                                    high: (
                                        <FormattedCurrency
                                            convert
                                            key="list-item-estimate-high"
                                            noFraction
                                            sourceCurrency={currency}
                                            value={highBidEstimate}
                                        />
                                    ),
                                    low: (
                                        <FormattedCurrency
                                            convert
                                            key="list-item-estimate-low"
                                            noFraction
                                            sourceCurrency={currency}
                                            value={lowBidEstimate}
                                        />
                                    ),
                                }}
                            />
                        </BodySecondary>
                    )}
                    {!isPassed ? (
                        <span className="flex items-center">
                            <LabelStrong>
                                {isSold && (
                                    <>
                                        <FormattedMessage id="bidding_console.console.lotTracker.soldFor" />
                                        &nbsp;
                                    </>
                                )}
                                <FormattedCurrency
                                    convert
                                    noFraction
                                    sourceCurrency={currency}
                                    value={
                                        isLiveItem ? leadingBid || startPrice : leadingBid || salePrice || startPrice
                                    }
                                />
                            </LabelStrong>
                            &nbsp;
                            {!isSold && (
                                <Caption color="secondary">
                                    ({itemBidCount}&nbsp;
                                    {itemBidCount === 1 ? (
                                        <FormattedMessage id="bidding_console.console.lotTracker.bid" />
                                    ) : (
                                        <FormattedMessage id="bidding_console.console.lotTracker.bids" />
                                    )}
                                    )
                                </Caption>
                            )}
                        </span>
                    ) : (
                        <LabelStrong>
                            <FormattedMessage id="bidding_console.console.lotTracker.passed" />
                        </LabelStrong>
                    )}
                </div>
            </div>
            {/* {(bidderHasBid || bidderHasHighBid) &&
                !isPassed &&
                !isSold &&
                itemId !== itemId && <IncreaseBidButton itemId={itemId} />} */}
        </div>
    );
};

export default memo<Props>(ListItem);
